/**
 * StoreSettingsFAB — Floating Billing & Settings Panel
 *
 * A manager-only floating action button (FAB) at bottom-right of every POS dashboard.
 * Clicking it opens a right-side drawer with three tabs:
 *   Overview — store info, billing status, current period summary
 *   Billing  — outstanding invoices with PayPal pay buttons, invoice history
 *   Luna AI  — live token usage for current period + historical cost breakdown
 *
 * Security:
 *  - All API calls use x-store-api-key header only
 *  - API key is NEVER displayed in the UI
 *  - Panel only mounts if apiKey is non-empty (parent enforces manager-only rendering)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Settings, X, CreditCard, Sparkles, CheckCircle2, Clock,
  Lock, AlertTriangle, ExternalLink, RefreshCw, ReceiptText,
  Cpu, ArrowUpRight, TrendingUp, Store, Calendar, ShieldCheck,
} from 'lucide-react';
import { CommissionLedgerModal } from '../CommissionLedgerModal';

// ── Types ─────────────────────────────────────────────────────────────────────

type BillingStatusType = 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED';
type InvoiceStatus     = 'UNPAID' | 'PAID' | 'WAIVED' | 'DISPUTED';
type TabId             = 'overview' | 'billing' | 'luna';

interface UnpaidInvoice {
  id: number; invoiceRef: string; billingPeriod: string;
  totalAmtUsd: number; dueDate: string;
}
interface CurrentPeriodUsage {
  inputTokens: number; outputTokens: number; estimatedCostUsd: number;
}
interface BillingStatusResp {
  billingStatus: BillingStatusType;
  unpaidInvoices: UnpaidInvoice[];
  currentPeriod: string;
  currentPeriodUsage: CurrentPeriodUsage;
}
interface Invoice {
  id: number; invoiceRef: string; billingPeriod: string;
  netProfitLkr: number; commissionLkr: number;
  tokenCostUsd: number; totalAmtUsd: number;
  status: InvoiceStatus; dueDate: string; paidAt: string | null;
  tokenInputCount?: number; tokenOutputCount?: number;
}

interface StoreSettingsFABProps { apiKey: string; storeName: string; }

// ── Config ────────────────────────────────────────────────────────────────────

const STATUS_CFG: Record<BillingStatusType, { label: string; color: string; bg: string; border: string; Icon: any }> = {
  ACTIVE:       { label: 'Active',       color: 'text-emerald-600', bg: 'bg-emerald-50',  border: 'border-emerald-200', Icon: CheckCircle2 },
  GRACE_PERIOD: { label: 'Grace Period', color: 'text-amber-600',   bg: 'bg-amber-50',    border: 'border-amber-200',   Icon: Clock },
  LOCKED:       { label: 'Locked',       color: 'text-red-600',     bg: 'bg-red-50',      border: 'border-red-200',     Icon: Lock },
};
const INV_STATUS_CFG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  UNPAID:   { label: 'Unpaid',   color: 'text-red-600',     bg: 'bg-red-50' },
  PAID:     { label: 'Paid',     color: 'text-emerald-600', bg: 'bg-emerald-50' },
  WAIVED:   { label: 'Waived',   color: 'text-sky-600',     bg: 'bg-sky-50' },
  DISPUTED: { label: 'Disputed', color: 'text-orange-600',  bg: 'bg-orange-50' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(p: string): string {
  if (!p) return '—';
  const [y, m] = p.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}
function fmtUsd(n: number): string { return `$${n.toFixed(n < 0.01 ? 6 : 2)}`; }
function fmtLkr(n: number): string { return `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function StoreSettingsFAB({ apiKey, storeName }: StoreSettingsFABProps) {
  const [isOpen,   setIsOpen]   = useState(false);
  const [tab,      setTab]      = useState<TabId>('overview');
  const [status,   setStatus]   = useState<BillingStatusResp | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [payingId, setPayingId] = useState<number | null>(null);
  const [tick,     setTick]     = useState(0);
  const [showCommissionLedger, setShowCommissionLedger] = useState(false);

  const load = useCallback(async () => {
    if (!apiKey || !isOpen) return;
    setLoading(true);
    try {
      const h = { 'x-store-api-key': apiKey };
      const [sR, iR] = await Promise.all([
        fetch('/api/billing/status',   { headers: h }),
        fetch('/api/billing/invoices', { headers: h }),
      ]);
      if (sR.ok) setStatus(await sR.json());
      if (iR.ok) setInvoices((await iR.json()).invoices || []);
    } catch { /* silent — no crash on network error */ }
    finally { setLoading(false); }
  }, [apiKey, isOpen, tick]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isOpen || tab !== 'luna') return;
    const intervalId = setInterval(() => setTick(t => t + 1), 4000);
    return () => clearInterval(intervalId);
  }, [isOpen, tab]);

  // Allow header button to open the panel via a custom event (no prop drilling)
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('kangavena:open-settings', handler);
    return () => window.removeEventListener('kangavena:open-settings', handler);
  }, []);

  const handlePayNow = async (inv: Invoice | UnpaidInvoice) => {
    setPayingId(inv.id);
    try {
      const resp = await fetch('/api/billing/paypal/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
        body: JSON.stringify({ invoiceId: inv.id }),
      });
      const { approveUrl, error } = await resp.json();
      if (approveUrl) window.open(approveUrl, '_blank', 'noopener,noreferrer');
      else alert(error || 'PayPal checkout failed. Please try again.');
    } catch { alert('Network error. Please try again.'); }
    finally { setPayingId(null); }
  };

  if (!apiKey) return null;

  const unpaidCount = status?.unpaidInvoices?.length ?? 0;
  const statusCfg   = STATUS_CFG[status?.billingStatus ?? 'ACTIVE'];
  const totalOwed   = (status?.unpaidInvoices ?? []).reduce((s, i) => s + i.totalAmtUsd, 0);
  const paidInvoices = invoices.filter(i => i.status === 'PAID');
  const totalPaid    = paidInvoices.reduce((s, i) => s + i.totalAmtUsd, 0);
  const usage        = status?.currentPeriodUsage;

  return (
    <>
      {/* ── FAB hidden — panel is opened via header button or custom event ─── */}

      {/* ── Drawer ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="fab-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[9986] bg-slate-900/20 backdrop-blur-[2px] print:hidden"
            />

            {/* Panel */}
            <motion.div
              key="fab-panel"
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              className="fixed right-0 top-0 h-full w-[400px] max-w-[100vw] z-[9987] bg-white border-l border-sky-100 shadow-[−8px_0_40px_rgba(14,165,233,0.12)] flex flex-col print:hidden"
            >
              {/* ── Panel Header ─────────────────────────────────────── */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-sky-50 bg-gradient-to-r from-sky-50/60 to-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-200">
                    <Store size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-slate-800 leading-tight">{storeName}</p>
                    <p className="text-[9px] text-slate-400 font-mono uppercase tracking-widest">Kangavena POS SaaS</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTick(t => t + 1)}
                    disabled={loading}
                    className="w-7 h-7 rounded-lg hover:bg-sky-50 flex items-center justify-center text-slate-400 hover:text-sky-500 transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* ── Status Strip ─────────────────────────────────────── */}
              {status && (
                <div className={`flex items-center gap-2 px-5 py-2.5 border-b ${statusCfg.border} ${statusCfg.bg} shrink-0`}>
                  <statusCfg.Icon size={13} className={statusCfg.color} />
                  <span className={`text-[11px] font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
                  {unpaidCount > 0 && (
                    <span className="ml-auto text-[10px] text-red-500 font-mono font-bold">
                      {unpaidCount} invoice{unpaidCount > 1 ? 's' : ''} outstanding
                    </span>
                  )}
                </div>
              )}

              {/* ── Tab Nav ──────────────────────────────────────────── */}
              <div className="flex border-b border-slate-100 shrink-0">
                {([
                  { id: 'overview', label: 'Overview',  Icon: TrendingUp },
                  { id: 'billing',  label: 'Billing',   Icon: ReceiptText },
                  { id: 'luna',     label: 'Luna AI',   Icon: Sparkles },
                ] as { id: TabId; label: string; Icon: any }[]).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-all border-b-2 ${
                      tab === t.id
                        ? 'border-sky-500 text-sky-600 bg-sky-50/40'
                        : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <t.Icon size={14} />
                    {t.label}
                  </button>
                ))}
              </div>

              {/* ── Tab Content ──────────────────────────────────────── */}
              <div className="flex-1 overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                    className="p-5 space-y-4"
                  >

                    {/* ════════════════ OVERVIEW TAB ════════════════════ */}
                    {tab === 'overview' && (
                      <>
                        {/* Period card */}
                        <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-2xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar size={13} className="text-sky-500" />
                            <span className="text-[10px] font-mono uppercase tracking-widest text-sky-600">Current Billing Period</span>
                          </div>
                          <p className="text-[18px] font-black text-slate-800">{formatPeriod(status?.currentPeriod || '')}</p>
                          <p className="text-[10px] text-slate-400 font-mono mt-0.5">{status?.currentPeriod || '—'}</p>
                        </div>

                        {/* Quick stats */}
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { label: 'Total Invoices', value: String(invoices.length), sub: 'all time',   color: 'text-slate-700' },
                            { label: 'Total Paid',     value: fmtUsd(totalPaid),        sub: 'USD',       color: 'text-emerald-600' },
                            { label: 'Outstanding',    value: fmtUsd(totalOwed),        sub: 'USD',       color: unpaidCount > 0 ? 'text-red-500' : 'text-slate-400' },
                          ].map(s => (
                            <div key={s.label} className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                              <p className={`text-[15px] font-black ${s.color}`}>{s.value}</p>
                              <p className="text-[8px] text-slate-400 font-mono uppercase tracking-wider mt-0.5">{s.label}</p>
                            </div>
                          ))}
                        </div>

                        {/* Current period AI usage mini-card */}
                        {usage && (
                          <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Sparkles size={13} className="text-violet-500" />
                                <span className="text-[10px] font-mono uppercase tracking-widest text-violet-600">Luna AI — This Month</span>
                              </div>
                              <button onClick={() => setTab('luna')} className="text-[9px] text-violet-400 hover:text-violet-600 flex items-center gap-1 font-semibold">
                                Details <ArrowUpRight size={9} />
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-[14px] font-black text-violet-700">{fmtTokens(usage.inputTokens)}</p>
                                <p className="text-[8px] text-slate-400 font-mono">Input</p>
                              </div>
                              <div>
                                <p className="text-[14px] font-black text-violet-700">{fmtTokens(usage.outputTokens)}</p>
                                <p className="text-[8px] text-slate-400 font-mono">Output</p>
                              </div>
                              <div>
                                <p className="text-[14px] font-black text-violet-700">{fmtUsd(usage.estimatedCostUsd)}</p>
                                <p className="text-[8px] text-slate-400 font-mono">Est. Cost</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Billing shortcut if outstanding */}
                        {unpaidCount > 0 && (
                          <button
                            onClick={() => setTab('billing')}
                            className="w-full flex items-center justify-between p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <AlertTriangle size={14} />
                              <span className="text-[12px] font-bold">{unpaidCount} unpaid invoice{unpaidCount > 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-bold">
                              {fmtUsd(totalOwed)} <ArrowUpRight size={12} />
                            </div>
                          </button>
                        )}

                        {/* Security note */}
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <ShieldCheck size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                          <p className="text-[10px] text-slate-400 leading-relaxed">
                            This panel uses your store API key for all requests. Your key is never displayed here. All billing data is encrypted in transit.
                          </p>
                        </div>
                      </>
                    )}

                    {/* ════════════════ BILLING TAB ═════════════════════ */}
                    {tab === 'billing' && (
                      <>
                        {/* Live Commission Accrued Card */}
                        <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-2xl p-4 mb-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0">
                                <TrendingUp size={16} className="text-red-600" />
                              </div>
                              <div>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-red-600 font-bold">Live Commission Accrued</p>
                                <p className="text-[8px] text-slate-600 mt-0.5">Platform commission deducted from sales</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-2xl font-black text-red-600 mb-2">{fmtLkr(status?.unpaidInvoices?.reduce((sum, inv) => sum + (invoices.find(i => i.id === inv.id)?.commissionLkr || 0), 0) || 0)}</p>
                          <p className="text-[9px] text-slate-600 mb-3">2% commission on all transactions</p>
                          <button
                            onClick={() => setShowCommissionLedger(true)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold transition-all"
                          >
                            <span>View Detailed Ledger</span>
                            <ExternalLink size={11} />
                          </button>
                        </div>

                        {/* Outstanding invoices */}
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 mb-3">
                            Outstanding Invoices
                          </p>
                          {!status || status.unpaidInvoices.length === 0 ? (
                            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                              <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                              <div>
                                <p className="text-[12px] font-bold text-emerald-700">All Clear</p>
                                <p className="text-[10px] text-emerald-600">No outstanding invoices. Your account is in good standing.</p>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {status.unpaidInvoices.map(inv => (
                                <div key={inv.id} className="border border-red-100 bg-red-50/60 rounded-xl p-4">
                                  <div className="flex items-start justify-between gap-2 mb-3">
                                    <div>
                                      <p className="text-[12px] font-black text-slate-800">{inv.invoiceRef}</p>
                                      <p className="text-[9px] text-slate-500 font-mono mt-0.5">
                                        Period: {formatPeriod(inv.billingPeriod)} · Due: {inv.dueDate}
                                      </p>
                                    </div>
                                    <span className="text-[13px] font-black text-red-600 whitespace-nowrap">
                                      {fmtUsd(inv.totalAmtUsd)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handlePayNow(inv)}
                                    disabled={payingId === inv.id}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 active:scale-[0.97] text-white text-[12px] font-bold transition-all disabled:opacity-50"
                                  >
                                    <CreditCard size={13} />
                                    {payingId === inv.id ? 'Opening PayPal…' : `Pay ${fmtUsd(inv.totalAmtUsd)} via PayPal`}
                                    {payingId !== inv.id && <ExternalLink size={11} />}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Invoice history */}
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 mb-3">
                            Invoice History
                          </p>
                          {invoices.length === 0 ? (
                            <p className="text-[11px] text-slate-400 text-center py-6">No invoices yet — your first invoice will appear on the 1st of next month.</p>
                          ) : (
                            <div className="space-y-2">
                              {invoices.map(inv => {
                                const sc = INV_STATUS_CFG[inv.status];
                                return (
                                  <div key={inv.id} className="border border-slate-100 rounded-xl p-3.5 bg-white hover:border-sky-100 transition-colors">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className="text-[11px] font-bold text-slate-700 truncate">{inv.invoiceRef}</p>
                                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${sc.color} ${sc.bg} flex-shrink-0`}>
                                            {sc.label}
                                          </span>
                                        </div>
                                        <p className="text-[9px] text-slate-400 font-mono mt-0.5">
                                          {formatPeriod(inv.billingPeriod)}
                                          {inv.paidAt && ` · Paid ${new Date(inv.paidAt).toLocaleDateString('en-LK')}`}
                                        </p>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <p className="text-[12px] font-black text-slate-800">{fmtUsd(inv.totalAmtUsd)}</p>
                                        <p className="text-[9px] text-slate-400 font-mono">{fmtLkr(inv.commissionLkr)} comm.</p>
                                      </div>
                                    </div>
                                    {/* Pay button for unpaid items in history */}
                                    {inv.status === 'UNPAID' && (
                                      <button
                                        onClick={() => handlePayNow(inv)}
                                        disabled={payingId === inv.id}
                                        className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-[11px] font-bold transition-all disabled:opacity-50"
                                      >
                                        <CreditCard size={11} />
                                        {payingId === inv.id ? 'Opening…' : 'Pay Now'}
                                        <ExternalLink size={10} />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* ════════════════ LUNA AI TAB ═════════════════════ */}
                    {tab === 'luna' && (
                      <>
                        {/* Live current period */}
                        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 rounded-2xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Cpu size={13} className="text-violet-500" />
                            <span className="text-[10px] font-mono uppercase tracking-widest text-violet-600">
                              Live Usage — {formatPeriod(status?.currentPeriod || '')}
                            </span>
                            <span className="ml-auto px-2 py-0.5 rounded-full bg-violet-100 text-violet-500 text-[8px] font-bold uppercase tracking-wider">Live</span>
                          </div>
                          {usage ? (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white/80 rounded-xl p-3 text-center border border-violet-100/60">
                                <p className="text-[18px] font-black text-violet-700">{fmtTokens(usage.inputTokens)}</p>
                                <p className="text-[9px] text-slate-500 font-mono mt-0.5">Input Tokens</p>
                              </div>
                              <div className="bg-white/80 rounded-xl p-3 text-center border border-violet-100/60">
                                <p className="text-[18px] font-black text-violet-700">{fmtTokens(usage.outputTokens)}</p>
                                <p className="text-[9px] text-slate-500 font-mono mt-0.5">Output Tokens</p>
                              </div>
                              <div className="col-span-2 bg-white/80 rounded-xl p-3 border border-violet-100/60">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-[10px] text-slate-400 font-mono">Estimated cost this period</p>
                                    <p className="text-[20px] font-black text-violet-700 mt-0.5">{fmtUsd(usage.estimatedCostUsd)}</p>
                                  </div>
                                  <div className="text-right text-[9px] text-slate-400 font-mono space-y-0.5">
                                    <div>Input:  {fmtUsd(usage.inputTokens  * 0.10 / 1_000_000)}</div>
                                    <div>Output: {fmtUsd(usage.outputTokens * 0.40 / 1_000_000)}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[11px] text-slate-400 text-center py-2">No Luna usage recorded this period yet.</p>
                          )}
                        </div>

                        {/* Historical by invoice */}
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 mb-3">
                            Historical Usage
                          </p>
                          {invoices.length === 0 ? (
                            <p className="text-[11px] text-slate-400 text-center py-6">No historical data yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {invoices.map(inv => (
                                <div key={inv.id} className="border border-slate-100 rounded-xl p-3.5 bg-white">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-bold text-slate-700">{formatPeriod(inv.billingPeriod)}</p>
                                    <span className="text-[10px] font-black text-violet-600">{fmtUsd(inv.tokenCostUsd)}</span>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                      <p className="text-[12px] font-black text-slate-700">{fmtTokens(inv.tokenInputCount || 0)}</p>
                                      <p className="text-[8px] text-slate-400 font-mono">Input</p>
                                    </div>
                                    <div>
                                      <p className="text-[12px] font-black text-slate-700">{fmtTokens(inv.tokenOutputCount || 0)}</p>
                                      <p className="text-[8px] text-slate-400 font-mono">Output</p>
                                    </div>
                                    <div>
                                      <p className="text-[12px] font-black text-slate-700">{fmtLkr(inv.netProfitLkr)}</p>
                                      <p className="text-[8px] text-slate-400 font-mono">Net Profit</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Pricing note */}
                        <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-[9px] text-slate-400 leading-relaxed font-mono">
                          Luna AI processing costs: Input $0.10/1M tokens · Output $0.40/1M tokens.
                          Text sessions tracked server-side. Voice sessions reported by client.
                          Costs are estimates — final amounts appear in monthly invoices.
                        </div>
                      </>
                    )}

                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Commission Ledger Modal */}
      <CommissionLedgerModal
        isOpen={showCommissionLedger}
        onClose={() => setShowCommissionLedger(false)}
        storeApiKey={apiKey}
      />
    </>
  );
}
